import { Crown, Ghost, User } from 'lucide-react'
import type { Character } from '@/types'
import type { Member as MemberType } from '@/types/member'

export type CharacterType = 'mestre' | 'jogador' | 'npc'

/**
 * Identifica o tipo de um personagem baseado em is_npc e membros
 */
export function getCharacterType(
  character: Character,
  membersByUserId?: Map<string, MemberType>
): CharacterType {
  // Se o personagem tem is_npc=true, é NPC
  if (character.isNpc === true) return 'npc'
  
  if (!membersByUserId) return 'jogador' // Fallback
  
  const member = membersByUserId.get(character.userId)
  if (!member) return 'npc' // Fallback: sem membro = NPC
  
  return member.role === 'mestre' ? 'mestre' : 'jogador'
}

/**
 * Retorna estilos e informações visuais baseados no tipo do personagem
 */
export function getCharacterTypeStyle(type: CharacterType) {
  switch (type) {
    case 'mestre':
      return {
        badge: 'bg-[#7c3aed]/20 text-[#a78bfa] border-[#7c3aed]/40',
        icon: Crown,
        label: 'Mestre',
        iconColor: 'text-[#a78bfa]'
      }
    case 'npc':
      return {
        badge: 'bg-[#64748b]/20 text-[#94a3b8] border-[#64748b]/40',
        icon: Ghost,
        label: 'NPC',
        iconColor: 'text-[#94a3b8]'
      }
    case 'jogador':
    default:
      return {
        badge: 'bg-[#00a884]/20 text-[#4ade80] border-[#00a884]/40',
        icon: User,
        label: 'Jogador',
        iconColor: 'text-[#4ade80]'
      }
  }
}

/**
 * A sigla do papel: uma letra, nas mesmas cores do crachá.
 *
 * Existe desde 2026-07-27 (decisão 4 do docs/PLANO_AREA_DO_CHAT.md). O crachá
 * de `getCharacterTypeStyle` — ícone + palavra + moldura — vivia numa linha
 * própria acima do texto, e essa linha custava ~26px por fala. No celular isso
 * é o que separa três mensagens visíveis de seis. A informação é a mesma; o
 * que muda é o desenho: de crachá para inicial.
 *
 * **A cor é a mesma de propósito.** Quem já aprendeu que violeta é mestre e
 * verde é jogador não reaprende nada — a letra só confirma o que a cor diz.
 * Por isso os hex vêm daqui e não são reescritos na bolha.
 */
export function getCharacterTypeSigil(type: CharacterType) {
  switch (type) {
    case 'mestre':
      return { letra: 'M', titulo: 'Mestre', classe: 'bg-[#7c3aed]/25 text-[#a78bfa] border-[#7c3aed]/40' }
    case 'npc':
      return { letra: 'N', titulo: 'NPC', classe: 'bg-[#64748b]/25 text-[#94a3b8] border-[#64748b]/40' }
    case 'jogador':
    default:
      return { letra: 'J', titulo: 'Jogador', classe: 'bg-[#00a884]/25 text-[#4ade80] border-[#00a884]/40' }
  }
}
